import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { ClassValue } from 'clsx'
import type { TFunction } from 'i18next'
import { localizeDigits } from '@/lib/localize-digits'

export { localizeDigits }

export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs))
}

/**
 * Extract user-friendly error message from API error
 * @param error - The error object from API
 * @param fallback - Fallback message if error details can't be extracted
 * @returns User-friendly error message
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  // Check if it's an ApiError instance with detail property
  if (error && typeof error === 'object') {
    if ('detail' in error && typeof error.detail === 'string') {
      return error.detail
    }
    if ('message' in error && typeof error.message === 'string') {
      return error.message
    }
  }

  // Check if it's a standard Error
  if (error instanceof Error) {
    return error.message
  }

  return fallback
}

/**
 * Format a date as a relative time string (e.g., "5 minutes ago").
 * @param date - The date to format
 * @param t - i18next translation function
 * @returns Localized relative time string
 */
export function formatTimeAgo(date: Date, t: TFunction): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffSecs < 60) {
    return localizeDigits(
      `${diffSecs} ${diffSecs !== 1 ? t('common.seconds') : t('common.second')} ${t('common.ago')}`,
    )
  } else if (diffMins < 60) {
    return localizeDigits(
      `${diffMins} ${diffMins !== 1 ? t('common.minutes') : t('common.minute')} ${t('common.ago')}`,
    )
  } else if (diffHours < 24) {
    return localizeDigits(
      `${diffHours} ${diffHours !== 1 ? t('common.hours') : t('common.hour')} ${t('common.ago')}`,
    )
  } else {
    return localizeDigits(
      `${diffDays} ${diffDays !== 1 ? t('common.days') : t('common.day')} ${t('common.ago')}`,
    )
  }
}
